import type { ICategoryStat } from "@/apis/exam_papers/getExamReport";
import { useQueryGetCategories } from "@/apis/categories/getCategories";

interface CategoryStatsTableProps {
  title: string;
  categoryStats: ICategoryStat[] | null;
  recentExamsStats: ICategoryStat[] | null;
  examType: string;
  subject: string;
}

export const CategoryStatsTable = ({
  title,
  categoryStats,
  recentExamsStats,
  examType,
  subject,
}: CategoryStatsTableProps) => {
  const { data: categories } = useQueryGetCategories(
    {
      exam_type: examType,
      subject: subject,
    },
    {
      enabled: !!examType && !!subject,
    }
  );

  const getRecentStats = (categoryId: string) => {
    return (
      recentExamsStats?.find((stat) => stat.category_id === categoryId) || {
        correct_count: 0,
        total_count: 0,
        accuracy: 0,
      }
    );
  };

  const getCategoryStats = (categoryId: string) => {
    return (
      categoryStats?.find((stat) => stat.category_id === categoryId) || {
        correct_count: 0,
        total_count: 0,
        accuracy: 0,
      }
    );
  };

  if (!categories) return null;

  // 서버에서 받은 categories 순서 그대로 사용하여 2개의 그룹으로 나눔
  const midPoint = Math.ceil(categories.length / 2);
  const firstColumn = categories.slice(0, midPoint);
  const secondColumn = categories.slice(midPoint);

  const renderTable = (categories: typeof firstColumn) => (
    <table className="w-full border border-gray-300 text-[9px]">
      <thead>
        <tr className="bg-gray-200">
          <th className="border px-1 py-0.5 text-center w-24">Category</th>
          <th className="border px-1 py-0.5 text-center w-20">Current</th>
          <th className="border px-1 py-0.5 text-center w-20">Recent 5</th>
        </tr>
      </thead>
      <tbody>
        {categories.map((category) => {
          const currentStats = getCategoryStats(category.id);
          const recentStats = getRecentStats(category.id);

          return (
            <tr key={category.id}>
              <td
                className="border px-1 py-0.5 font-medium truncate max-w-[96px] text-center"
                title={category.short_name}
              >
                {category.short_name}
              </td>
              <td className="border px-1 py-0.5 text-center">
                {currentStats.correct_count}/{currentStats.total_count} (
                {Math.round(currentStats.accuracy * 100)}%)
              </td>
              <td className="border px-1 py-0.5 text-center">
                {recentStats.correct_count}/{recentStats.total_count} (
                {Math.round(recentStats.accuracy * 100)}%)
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div className="w-full">
      <div className="text-black font-semibold text-sx mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-4">
        <div>{renderTable(firstColumn)}</div>
        <div>{renderTable(secondColumn)}</div>
      </div>
    </div>
  );
};
